import React, { useState } from 'react';
import {
  LayoutDashboard, FileText, Sparkles, MessageSquare, ShieldCheck, Users,
  Building2, ChevronLeft, ChevronRight, Globe, ArrowUpRight, BookCheck,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*  WhitelabelDemo                                                            */
/*  Renders 4 interactive preview "pages" — Dashboard · AI Council · Documents · */
/*  Operating Manual — with the trialist's brand name + logo + color swapped  */
/*  in so they can see exactly what their workspace will look like.           */
/* -------------------------------------------------------------------------- */

function Chrome({ brand, logoUrl, color, fqdn, children, activeNav }) {
  const NAV_ITEMS = [
    { id: 'overview',  icon: LayoutDashboard, label: 'Overview' },
    { id: 'council',   icon: MessageSquare,   label: 'AI Council' },
    { id: 'documents', icon: FileText,        label: 'Documents' },
    { id: 'approvals', icon: ShieldCheck,     label: 'Approvals' },
    { id: 'manual',    icon: BookCheck,       label: 'Manual' },
    { id: 'users',     icon: Users,           label: 'Users' },
  ];
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
      {/* Browser bar */}
      <div className="h-7 bg-slate-100 flex items-center gap-1.5 px-3 border-b border-slate-200">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
        <div className="ml-3 inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-500 truncate">
          <Globe size={9} /> {fqdn || 'your-brand.vigilantentities.com'}
        </div>
      </div>
      {/* App chrome */}
      <div className="flex bg-[#FAFAFA] min-h-[340px]">
        {/* Sidebar */}
        <aside className="w-44 bg-white border-r border-slate-200 flex flex-col">
          <div className="px-3 py-3 flex items-center gap-2 border-b border-slate-100">
            {logoUrl
              ? <img src={logoUrl} alt="" className="h-7 w-7 object-contain rounded" />
              : <div className="h-7 w-7 rounded" style={{ background: color }} />}
            <div className="min-w-0">
              <div className="text-[10px] font-bold truncate" style={{ color }}>{brand || 'Your Brand'}</div>
              <div className="text-[8px] uppercase tracking-[0.18em] text-slate-400">Powered by Vigilant</div>
            </div>
          </div>
          <nav className="px-2 py-2 space-y-0.5 flex-1">
            {NAV_ITEMS.map((n) => {
              const isActive = activeNav === n.id;
              return (
                <div
                  key={n.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-[10px] font-medium ${
                    isActive
                      ? 'bg-slate-50 text-slate-900'
                      : 'text-slate-500'
                  }`}
                  style={isActive ? { borderLeft: `2px solid ${color}` } : undefined}
                >
                  <n.icon size={11} className={isActive ? '' : 'text-slate-400'} style={isActive ? { color } : undefined} />
                  <span className="truncate">{n.label}</span>
                </div>
              );
            })}
          </nav>
        </aside>
        {/* Content */}
        <div className="flex-1 p-4 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

/* ─── Page 1: Overview ────────────────────────────────────────────────── */
function PreviewOverview({ brand, color }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-[0.22em] font-bold" style={{ color }}>Welcome back</div>
      <h3 className="text-base font-bold mt-1 leading-tight">Good morning, {brand || 'team'}.</h3>
      <p className="text-[10px] text-slate-500 mt-1">Your 26 AI advisors are caught up. Here&rsquo;s where you stand.</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[
          { label: 'Open approvals', value: '3', tone: 'amber' },
          { label: 'Live entities',  value: '8', tone: 'slate' },
          { label: 'Vetted leads',   value: '12', tone: 'emerald' },
        ].map((s) => (
          <div key={s.label} className="rounded border border-slate-200 p-2 bg-white">
            <div className="text-[8px] uppercase tracking-[0.18em] text-slate-400 font-bold">{s.label}</div>
            <div className="text-lg font-bold mt-0.5" style={{ color }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded border border-slate-200 p-2 bg-white">
        <div className="text-[8px] uppercase tracking-[0.18em] text-slate-400 font-bold flex items-center justify-between">
          <span>Last council</span><span className="text-[8px] normal-case tracking-normal text-slate-400">moderated by Yusuf</span>
        </div>
        <p className="text-[10px] mt-1 leading-snug">&ldquo;Pursue Q2 Series A conditional on the 18-month runway model.&rdquo;</p>
      </div>
    </div>
  );
}

/* ─── Page 2: AI Council ──────────────────────────────────────────────── */
function PreviewCouncil({ color }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-[0.22em] font-bold" style={{ color }}>Convene a council</div>
      <div className="rounded border border-slate-200 p-2 bg-white mt-2">
        <div className="text-[9px] text-slate-700 font-medium leading-snug">
          &ldquo;Should we pursue a 2026 Series A round in Q2?&rdquo;
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {['Yusuf', 'Khadija', 'Mohammed', 'Maryam', 'Nouh', 'Fatima'].map((n) => (
            <span key={n} className="text-[8px] font-bold px-1.5 py-0.5 rounded border border-slate-200">{n}</span>
          ))}
        </div>
      </div>
      <div className="mt-2 rounded p-2" style={{ background: `${color}10`, border: `1px solid ${color}50` }}>
        <div className="text-[8px] uppercase tracking-[0.22em] font-bold" style={{ color }}>Synthesis</div>
        <p className="text-[9px] mt-0.5 font-bold">Pursue Q2 conditional on closing 3 readiness gaps.</p>
      </div>
      <div className="mt-2 space-y-1">
        {['Khadija (CFO)', 'Mohammed (COO)'].map((p, i) => (
          <div key={p} className="rounded bg-rose-50 border border-rose-100 p-1.5 text-[8px]">
            <b>{p}:</b> {i === 0 ? 'Runway model needs an 18-month buffer first.' : 'Sales cycle data still too thin.'}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Page 3: Documents ───────────────────────────────────────────────── */
function PreviewDocuments({ brand, color }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-[0.22em] font-bold" style={{ color }}>Document hub</div>
      <h3 className="text-sm font-bold mt-0.5">All {brand || 'your'} documents — auto-OCR&rsquo;d.</h3>
      <div className="mt-2 space-y-1">
        {[
          { name: 'Series A Term Sheet v3.pdf', size: '1.2 MB', expiry: 'Mar 2026', status: 'OCR done' },
          { name: 'Cap Table — Q2.xlsx',         size: '286 KB', expiry: '—',         status: 'Live' },
          { name: 'Board Minutes 2026-05.pdf',   size: '412 KB', expiry: '—',         status: 'Signed' },
        ].map((d) => (
          <div key={d.name} className="flex items-center gap-2 p-1.5 rounded border border-slate-200 bg-white text-[9px]">
            <div className="h-6 w-6 rounded flex items-center justify-center" style={{ background: `${color}20`, color }}>
              <FileText size={11} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{d.name}</div>
              <div className="text-[8px] text-slate-400">{d.size} · expiry {d.expiry}</div>
            </div>
            <span className="text-[8px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{d.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Page 4: Operating Manual ────────────────────────────────────────── */
function PreviewManual({ brand, color }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-[0.22em] font-bold" style={{ color }}>Operating manual</div>
      <h3 className="text-sm font-bold mt-0.5">How {brand || 'your workspace'} works.</h3>
      <p className="text-[9px] text-slate-500 mt-0.5 leading-snug">Bank-grade tenancy, sub-domain provisioning, AI control plane.</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded p-2 border border-emerald-200 bg-emerald-50/40">
          <div className="text-[8px] uppercase tracking-[0.18em] text-emerald-700 font-bold">You CAN</div>
          <ul className="mt-0.5 text-[8px] space-y-0.5">
            <li>· Chat with 26 AI advisors</li>
            <li>· Convene councils</li>
            <li>· OCR documents</li>
          </ul>
        </div>
        <div className="rounded p-2 border border-rose-200 bg-rose-50/40">
          <div className="text-[8px] uppercase tracking-[0.18em] text-rose-700 font-bold">Vigilant manages</div>
          <ul className="mt-0.5 text-[8px] space-y-0.5">
            <li>· AI persona training</li>
            <li>· Cross-tenant audit</li>
            <li>· Role builder limits</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const PAGES = [
  { id: 'overview',  label: 'Overview',    icon: LayoutDashboard, Component: PreviewOverview },
  { id: 'council',   label: 'AI Council',  icon: MessageSquare,   Component: PreviewCouncil  },
  { id: 'documents', label: 'Documents',   icon: FileText,        Component: PreviewDocuments },
  { id: 'manual',    label: 'Manual',      icon: BookCheck,       Component: PreviewManual },
];

export default function WhitelabelDemo({ brand, logoUrl, color, fqdn }) {
  const [idx, setIdx] = useState(0);
  const page = PAGES[idx];
  const PageComp = page.Component;

  return (
    <div data-testid="whitelabel-demo">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="ve-eyebrow" style={{ color }}>
          Preview · {page.label}
        </div>
        <div className="inline-flex items-center gap-1">
          <button
            onClick={() => setIdx((i) => (i - 1 + PAGES.length) % PAGES.length)}
            className="h-7 w-7 rounded border border-slate-200 hover:border-slate-300 flex items-center justify-center text-slate-500"
            aria-label="Previous"
            data-testid="preview-prev"
          ><ChevronLeft size={14}/></button>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-2">
            {idx + 1} / {PAGES.length}
          </div>
          <button
            onClick={() => setIdx((i) => (i + 1) % PAGES.length)}
            className="h-7 w-7 rounded border border-slate-200 hover:border-slate-300 flex items-center justify-center text-slate-500"
            aria-label="Next"
            data-testid="preview-next"
          ><ChevronRight size={14}/></button>
        </div>
      </div>

      <Chrome brand={brand} logoUrl={logoUrl} color={color} fqdn={fqdn} activeNav={page.id}>
        <PageComp brand={brand} logoUrl={logoUrl} color={color} />
      </Chrome>

      {/* Page tabs */}
      <div className="mt-3 flex flex-wrap gap-1">
        {PAGES.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setIdx(i)}
            data-testid={`preview-tab-${p.id}`}
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded border transition ${
              i === idx ? 'text-white' : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
            style={i === idx ? { background: color, borderColor: color } : undefined}
          >
            <p.icon size={10}/> {p.label}
          </button>
        ))}
      </div>

      {/* Subdomain badge */}
      {fqdn && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={{ borderColor: `${color}60`, background: `${color}15` }}>
          <Globe size={11} style={{ color }} />
          <span className="text-[10px] font-mono" style={{ color }}>{fqdn}</span>
          <ArrowUpRight size={10} style={{ color }} />
        </div>
      )}
    </div>
  );
}
